import React, { useState, useEffect } from "react";
import { db } from "../../../config/firebaseConfig";
import { ref, get } from "firebase/database";
import { isMobileScreen } from "../../../lib/utils";
import { Tabs, TabsList, TabsTrigger } from "../../../@/components/ui/tabs";

interface TermsSection {
  title: string;
  paragraphs: string[];
}

const TermsOfServiceContent: React.FC = () => {
  const [termsSections, setTermsSections] = useState<
    Record<string, TermsSection>
  >({});
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState<number>(1);

  useEffect(() => {
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }

    // Fetch terms data from Firebase
    const fetchTermsData = async () => {
      try {
        const termsRef = ref(db, "LayoutSettingData/TermsOfService");
        const snapshot = await get(termsRef);

        if (snapshot.exists()) {
          setTermsSections(snapshot.val());
        }
      } catch (error) {
        console.error("Error fetching terms data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTermsData();
  }, []);

  const navigationOptions = [
    {
      title: termsSections.definitions?.title || "Definitions",
      id: 1,
      key: "definitions",
    },
    {
      title:
        termsSections.generalSupplemental?.title ||
        "General Supplemental Terms",
      id: 2,
      key: "generalSupplemental",
    },
    {
      title:
        termsSections.specificSupplemental?.title ||
        "Specific Supplemental Terms",
      id: 3,
      key: "specificSupplemental",
    },
    {
      title: termsSections.marketplace?.title || "Marketplace Method Terms",
      id: 4,
      key: "marketplace",
    },
    {
      title:
        termsSections.merchantManaged?.title ||
        "Merchant Managed Delivery Method Terms",
      id: 5,
      key: "merchantManaged",
    },
    {
      title: termsSections.nonDelivery?.title || "Non-Delivery Terms",
      id: 6,
      key: "nonDelivery",
    },
    {
      title: termsSections.dineIn?.title || "Dine-In Terms",
      id: 7,
      key: "dineIn",
    },
    {
      title: termsSections.webshop?.title || "Webshop Online Ordering Terms",
      id: 8,
      key: "webshop",
    },
    {
      title: termsSections.sponsoredListing?.title || "Sponsored Listing Terms",
      id: 9,
      key: "sponsoredListing",
    },
    {
      title:
        termsSections.dataProcessing?.title ||
        "Addendum A: Data Processing Agreement",
      id: 10,
      key: "dataProcessing",
    },
    {
      title:
        termsSections.googleCalendar?.title ||
        "Google Calendar Integration Terms",
      id: 11,
      key: "googleCalendar",
    },
  ];

  if (loading) {
    return (
      <div className="px-[5vw] md:px-[16vw] pt-[5vh] w-full">
        <div>
          <div className="h-[7.5vh] w-[40%] bg-gray-200 animate-pulse rounded-md"></div>
        </div>
        <div className="mt-[6vh] flex">
          <div className="hidden md:flex flex-col space-y-[2vh] min-w-[200px]">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="h-[2vh] w-[80%] bg-gray-200 animate-pulse rounded-md"
              ></div>
            ))}
          </div>
          <div className="md:ml-[10%] flex-1">
            <div className="h-[3.75vh] w-[30%] bg-gray-200 animate-pulse rounded-md mb-4"></div>
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className="h-[2vh] w-full bg-gray-200 animate-pulse rounded-md mb-3"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    const option = navigationOptions.find((opt) => opt.id === selectedOption);
    if (!option) return null;

    const section = termsSections[option.key];
    if (!section) return null;

    return (
      <div className="md:ml-[10%] flex text-[1.7vh] font-helvetica flex-col text-black">
        <p className="font-helveticaBold space-y-[1.6vh] text-[3.75vh]">
          {section.title}
        </p>
        <div className="text-pretty mt-[1vh] space-y-[3%] leading-tight tracking-[-1%]">
          {section.paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-max flex-col">
      <div className="px-[5vw] md:px-[16vw] pt-[5vh] w-full">
        <div>
          {/* <p className="flex items-start text-[1.65vh] text-customGrayBase">
            Last modified: 10/22/2024
          </p> */}
          <h1 className="mt-[4%] font-helveticaBold text-[7.5vh] leading-none text-black">
            Afrocart Services <br /> Agreement
          </h1>
        </div>
        <div className="mt-[6vh] flex flex-col md:flex-row">
          {/* Mobile navigation - horizontally scrollable tabs */}
          {isMobileScreen && (
            <Tabs
              defaultValue={selectedOption.toString()}
              onValueChange={(value) => setSelectedOption(parseInt(value))}
              className="w-full mb-6"
            >
              <div className="w-full overflow-x-auto pb-2">
                <TabsList className="flex min-w-fit bg-transparent border-b border-gray-200">
                  {navigationOptions.map((option) => (
                    <TabsTrigger
                      key={option.id}
                      value={option.id.toString()}
                      className="flex-1 px-6 data-[state=active]:bg-customGreenColor data-[state=active]:text-white whitespace-nowrap"
                    >
                      {option.title}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            </Tabs>
          )}

          {/* Desktop navigation */}
          <div className="hidden md:flex flex-col min-w-max space-y-[2vh] text-[1.93vh]">
            {navigationOptions.map((option) => (
              <div
                key={option.id}
                className={`cursor-pointer text-customGrayBase ${
                  selectedOption === option.id
                    ? "text-customGreenColor font-helveticaBold"
                    : "font-normal"
                }`}
                onClick={() => setSelectedOption(option.id)}
              >
                {option.title}
              </div>
            ))}
          </div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default TermsOfServiceContent;
